/* eslint-disable max-lines */
/* eslint-disable import/prefer-default-export */
import {
  every,
  find,
  first,
  forEach,
  isArray,
  isNil,
  filter as lodashFilter,
  map,
  mergeWith,
  omit,
  reduce,
  reject,
  reverse,
  some,
  sortBy,
  sortedUniqBy,
} from 'lodash';

import {
  cancelLineItemApi,
  cancelOrderApi,
  changePrescriptionReviewReasonApi,
  createOrderShipmentApi,
  manageProductFulfillmentApi,
  moveOrderToFillLaterApi,
  orderNeedsReviewApi,
  prescriptionNeedsReviewAPI,
  reverifyOrderApi,
  sendOrderBackToQueueApi,
  shipOrderLaterApi,
  splitLineItemQuantityApi,
  updateOrderAddressApi,
  verifyOrderApi,
  voidShipmentApi,
} from 'api/order';
import { moveLineItemsToFillLaterApi } from 'api/fillLater';
import {
  addGuarantorApi,
  addPatientAddressApi,
  addPatientApi,
  addPatientInsuranceApi,
  addPatientMedicalHistoryApi,
  addPatientMedicationApi,
  addPatientPharmacyApi,
  addPatientProviderApi,
  associatePatientWithExternalProfileApi,
  createLineItemNoteApi,
  createPatientCommentApi,
  createRefillOrderApi,
  deactivateInsuranceApi,
  deactivatePatientProviderApi,
  deleteGuarantorApi,
  deletePatientAddressApi,
  deletePatientCommentApi,
  deletePatientMedicationApi,
  deletePatientPharmacyApi,
  editOrderShipmentApi,
  editPatientApi,
  editPatientPrescriptionApi,
  getAuthorizationsApi,
  getDuplicatePatientsApi,
  getFullPatientDetailsApi,
  getGuarantorsApi,
  getPatientActionItemsApi,
  getPatientAddressesApi,
  getPatientApi,
  getPatientInsurancesApi,
  getPatientMedicalHistoryApi,
  getPatientOrderHistoryApi,
  getPatientPharmaciesApi,
  getPatientPrescriptionDetailsApi,
  getPatientProvidersApi,
  getPatientUsersApi,
  listLineItemNotesApi,
  listPatientCommentsApi,
  listPatientInvoicesApi,
  listPatientMedicationsApi,
  listPatientOrderShipmentsApi,
  listPatientOrdersApi,
  listPatientPrescriptionsApi,
  noteActionApi,
  onboardPatientApi,
  patientHistoryTrackListApi,
  recordAuditEventApi,
  resetPasswordApi,
  saveTestClaimResultApi,
  searchQs1ProfileApi,
  setPartnerExternalIdApi,
  setPatientContactsForPartnersApi,
  updateAddOnQuantityApi,
  updateGuarantorApi,
  updatePatientAddressApi,
  updatePatientAuthorizationsApi,
  updatePatientInsuranceApi,
  updatePatientMedicationApi,
  updatePatientPharmacyApi,
  updatePatientProviderApi,
  validateAddressApi,
  validatePrescriptionForRefillApi,
} from 'api/patient';
import { getPrescriptionFillHistoryApi, getPrescriptionSignedUrlApi } from 'api/prescription';
import {
  assertQs1RxNumberApi,
  completeEntryApi,
  controlledSubstancesCheckApi,
  copayResultCheckApi,
  prescriberClarificationCheckApi,
  profitabilityResultCheckApi,
  reviewLineItemApi,
  updateRequestLineItemStateApi,
  verifyRequestLineItemApi,
} from 'api/requests';
import errorHeaders from 'common/constants/errorHeaders';
import { ContentType, ContextType, NoteState } from 'common/constants/notes';
import { casingFormat, isDuplicatesFound } from 'common/utils';
import lineItemEnum from 'enums/lineItem.enum';
import { BASIC_ORDER_HISTORY_STATUSES } from 'common/constants/orderStatusGroups';
import { patientActions } from 'store/slices/patientSlice';
import { workQueueActions } from 'store/slices/workQueueSlice';
import { handleError } from './errorHandlerThunks';
import { getPrescriptionsNeedsReviewByPage } from './workQueueThunks';

export const addPatient =
  ({ patientData }) =>
  (dispatch) => {
    return addPatientApi({ patientData })
      .then((patient) => {
        dispatch(patientActions.loadPatientData({ patient }));

        return patient.mpi;
      })
      .catch((error) => dispatch(handleError({ error })));
  };

export const editPatient =
  ({ patientData }) =>
  (dispatch) => {
    const formattedPatientData = {
      ...patientData,
      firstName: casingFormat(patientData.firstName),
      lastName: casingFormat(patientData.lastName),
      middleName: casingFormat(patientData.middleName),
    };

    return editPatientApi({ patientData: formattedPatientData })
      .then(() => {
        dispatch(patientActions.loadPatientData({ patient: formattedPatientData }));
        dispatch(
          patientActions.updatePatientNotificationPreference({
            phone: formattedPatientData.preferencesState.phone,
            email: formattedPatientData.preferencesState.email,
            mpi: formattedPatientData.mpi,
          })
        );

        return true;
      })
      .catch((error) => {
        return first(error.response?.errors)?.extensions?.code ===
          errorHeaders.QS1_VALIDATION_FAILED
          ? dispatch(handleError({ error, heading: 'QS1 Id Not Found' }))
          : dispatch(handleError({ error }));
      });
  };

export const listPatientComments =
  ({ mpi, offset, searchText }) =>
  (dispatch, getState) => {
    const oldComments = getState().patient[mpi]?.comments || [];

    return listPatientCommentsApi({ mpi, offset, searchText })
      .then((comments) => {
        const hasMoreComments = !!comments?.length;

        return dispatch(
          patientActions.loadComments({
            mpi,
            comments: sortedUniqBy(
              reverse(sortBy([...oldComments, ...comments], ['createdAt'])),
              'commentId'
            ),
            hasMoreComments,
          })
        );
      })
      .catch((error) => dispatch(handleError({ error })));
  };

export const addPatientComments =
  ({ mpi, message }) =>
  (dispatch, getState) => {
    const oldComments = getState().patient[mpi]?.comments || [];
    const hasMoreComments = getState().patient[mpi]?.hasMoreComments;

    return createPatientCommentApi({ mpi, message, contextId: mpi })
      .then(({ comment }) => {
        return dispatch(
          patientActions.loadComments({
            mpi,
            comments: [comment, ...oldComments],
            hasMoreComments,
          })
        );
      })
      .catch((error) => dispatch(handleError({ error })));
  };

export const deletePatientComment =
  ({ mpi, patientCommentId }) =>
  (dispatch, getState) => {
    const { comments, hasMoreComments } = getState().patient[mpi];

    dispatch(
      patientActions.deleteComment({
        mpi,
        patientCommentId,
      })
    );

    return deletePatientCommentApi({ patientCommentId }).catch((error) => {
      dispatch(handleError({ error }));
      dispatch(
        patientActions.loadComments({
          mpi,
          comments,
          hasMoreComments,
        })
      );
    });
  };

export const getPatient =
  ({ mpi }) =>
  (dispatch) => {
    return getPatientApi({ mpi })
      .then((patient) => {
        const { addresses, ...patientData } = patient;
        dispatch(patientActions.loadPatientData({ patient: patientData }));
        dispatch(patientActions.loadAddresses({ addresses, mpi }));
      })
      .catch((error) => dispatch(handleError({ error })));
  };

export const addGuarantor =
  ({ guarantor }) =>
  (dispatch, getState) => {
    const guarantors = getState().patient[guarantor.mpi]?.guarantors || [];

    return addGuarantorApi({ guarantor })
      .then((newGuarantor) => {
        dispatch(
          patientActions.loadGuarantors({
            guarantors: [...guarantors, newGuarantor],
            mpi: guarantor.mpi,
          })
        );
        return true;
      })
      .catch((error) => {
        dispatch(handleError({ error }));
        return false;
      });
  };

export const getGuarantors =
  ({ mpi }) =>
  (dispatch) => {
    return getGuarantorsApi({ mpi })
      .then((guarantors) => {
        dispatch(patientActions.loadGuarantors({ guarantors, mpi }));
      })
      .catch((error) => dispatch(handleError({ error })));
  };

export const updateGuarantor =
  ({ guarantor }) =>
  (dispatch, getState) => {
    const guarantors = getState().patient[guarantor.mpi]?.guarantors || [];

    return updateGuarantorApi({ guarantor })
      .then((updatedGuarantor) => {
        dispatch(
          patientActions.loadGuarantors({
            mpi: guarantor.mpi,
            guarantors: [
              ...reject(guarantors, { guarantorId: updatedGuarantor.guarantorId }),
              updatedGuarantor,
            ],
          })
        );
        return true;
      })
      .catch((error) => {
        dispatch(
          patientActions.loadGuarantors({
            mpi: guarantor.mpi,
            guarantors,
          })
        );
        dispatch(handleError({ error }));
        return false;
      });
  };

export const addPatientMedication =
  ({ medicationData }) =>
  (dispatch, getState) => {
    const medications = getState().patient[medicationData.mpi]?.medications || [];

    return addPatientMedicationApi({ medicationData })
      .then((medication) => {
        dispatch(
          patientActions.loadMedications({
            mpi: medicationData.mpi,
            medications: [medication, ...medications],
          })
        );
        return true;
      })
      .catch((error) => {
        dispatch(handleError({ error }));
        return false;
      });
  };

export const deleteGuarantor =
  ({ guarantorId, mpi }) =>
  (dispatch, getState) => {
    const guarantors = getState().patient[mpi]?.guarantors || [];

    dispatch(
      patientActions.loadGuarantors({
        mpi,
        guarantors: reject(guarantors, { guarantorId }),
      })
    );

    return deleteGuarantorApi({ mpi, guarantorId }).catch((error) => {
      dispatch(patientActions.loadGuarantors({ mpi, guarantors }));
      dispatch(handleError({ error }));
    });
  };

export const updatePatientMedication =
  ({ medication }) =>
  (dispatch, getState) => {
    const medications = getState().patient[medication.mpi]?.medications || [];

    return updatePatientMedicationApi({ medication })
      .then((updatedMedication) => {
        dispatch(
          patientActions.loadMedications({
            mpi: medication.mpi,
            medications: [
              ...reject(medications, {
                patientMedicationId: updatedMedication.patientMedicationId,
              }),
              updatedMedication,
            ],
          })
        );
        return true;
      })
      .catch((error) => {
        dispatch(
          patientActions.loadMedications({
            mpi: medication.mpi,
            medications,
          })
        );
        dispatch(handleError({ error }));
        return false;
      });
  };

export const deletePatientMedication =
  ({ patientMedicationId, mpi }) =>
  (dispatch, getState) => {
    const medications = getState().patient[mpi]?.medications || [];

    dispatch(
      patientActions.loadMedications({
        mpi,
        medications: reject(medications, { patientMedicationId }),
      })
    );

    return deletePatientMedicationApi({ patientMedicationId, mpi }).catch((error) => {
      dispatch(patientActions.loadMedications({ mpi, medications }));
      dispatch(handleError({ error }));
    });
  };

export const listPatientMedications =
  ({ mpi }) =>
  (dispatch) => {
    return listPatientMedicationsApi({ mpi })
      .then((medications) => dispatch(patientActions.loadMedications({ mpi, medications })))
      .catch((error) => dispatch(handleError({ error })));
  };

export const listPatientPrescriptions =
  ({ mpi, forceRefresh }) =>
  (dispatch) => {
    return listPatientPrescriptionsApi({ mpi, forceRefresh })
      .then(({ prescriptions }) => {
        return dispatch(patientActions.loadPrescriptions({ mpi, prescriptions }));
      })
      .catch((error) => dispatch(handleError({ error })));
  };

export const getPatientPrescriptionDetails =
  ({ mpi, prescriptionUUID, forceRefresh, offset, limit, prescriptionFilter }) =>
  (dispatch) => {
    return getPatientPrescriptionDetailsApi({
      mpi,
      prescriptionUUID,
      forceRefresh,
      offset,
      limit,
      prescriptionFilter,
    })
      .then(({ prescriptions }) => {
        const prescriptionDetails = prescriptions?.[0];
        if (prescriptionDetails) {
          return dispatch(
            patientActions.loadPrescriptionDetails({
              mpi,
              prescriptionUUID,
              prescriptionDetails,
            })
          );
        }
        return null;
      })
      .catch((error) => dispatch(handleError({ error })));
  };

export const getPatientMedicalHistory =
  ({ mpi }) =>
  (dispatch) => {
    return getPatientMedicalHistoryApi({ mpi })
      .then(({ medicalHistory }) =>
        dispatch(patientActions.loadMedicalHistory({ mpi, medicalHistory }))
      )
      .catch((error) => dispatch(handleError({ error })));
  };

export const addPatientMedicalHistory =
  ({ newMedicalHistory }) =>
  (dispatch, getState) => {
    const existingMedicalHistory = getState().patient[newMedicalHistory.mpi].medicalHistory;

    return addPatientMedicalHistoryApi({ newMedicalHistory })
      .then(({ medicalHistory }) => {
        dispatch(
          patientActions.loadMedicalHistory({
            mpi: newMedicalHistory.mpi,
            medicalHistory: mergeWith({ ...existingMedicalHistory }, medicalHistory, (dest, src) =>
              isArray(dest) && !isNil(src) ? dest.concat(src) : undefined
            ),
          })
        );
        return true;
      })
      .catch((error) => {
        dispatch(handleError({ error }));
        return false;
      });
  };

export const getAuthorizations =
  ({ mpi }) =>
  (dispatch) => {
    return getAuthorizationsApi({ mpi })
      .then((authorizationObj) =>
        dispatch(
          patientActions.loadAuthorizations({
            mpi,
            authorization: authorizationObj?.authorization || null,
          })
        )
      )
      .catch((error) => dispatch(handleError({ error })));
  };

export const updatePatientAuthorizations =
  ({ mpi, newAuthorization }) =>
  (dispatch) => {
    return updatePatientAuthorizationsApi({ mpi, authorization: newAuthorization })
      .then(({ authorization }) => {
        dispatch(patientActions.loadAuthorizations({ mpi, authorization }));
        return true;
      })
      .catch((error) => {
        dispatch(handleError({ error }));
        return false;
      });
  };

export const addPatientInsurance =
  ({ insurance }) =>
  (dispatch, getState) => {
    const insurances = getState().patient[insurance.mpi]?.insurances || [];

    return addPatientInsuranceApi({ insurance })
      .then((newInsurance) => {
        dispatch(
          patientActions.loadInsurances({
            mpi: insurance.mpi,
            insurances: [...insurances, newInsurance],
          })
        );
        return true;
      })
      .catch((error) => {
        dispatch(handleError({ error }));
        return false;
      });
  };

export const getPatientInsurances =
  ({ mpi }) =>
  (dispatch) => {
    return getPatientInsurancesApi({ mpi })
      .then((insurances) => {
        dispatch(patientActions.loadInsurances({ mpi, insurances }));
      })
      .catch((error) => {
        dispatch(handleError({ error }));
      });
  };

export const updatePatientInsurance =
  ({ insurance }) =>
  (dispatch, getState) => {
    const insurances = getState().patient[insurance.mpi]?.insurances || [];

    return updatePatientInsuranceApi({ insurance })
      .then((updatedInsurance) => {
        dispatch(
          patientActions.loadInsurances({
            mpi: insurance.mpi,
            insurances: [
              ...reject(insurances, { patientInsuranceId: updatedInsurance.patientInsuranceId }),
              updatedInsurance,
            ],
          })
        );
        return true;
      })
      .catch((error) => {
        dispatch(getPatientInsurances({ mpi: insurance.mpi }));
        dispatch(handleError({ error }));
        return false;
      });
  };

export const deactivateInsurance =
  ({ patientInsuranceId, mpi }) =>
  (dispatch, getState) => {
    const insurances = getState().patient[mpi]?.insurances || [];
    const oldInsuranceState = find(insurances, { patientInsuranceId });

    dispatch(
      patientActions.loadInsurances({
        mpi,
        insurances: [
          ...reject(insurances, { patientInsuranceId: oldInsuranceState.patientInsuranceId }),
          { ...oldInsuranceState, isActive: false, priority: null },
        ],
      })
    );

    return deactivateInsuranceApi({ patientInsuranceId }).catch((error) => {
      dispatch(getPatientInsurances({ mpi }));
      dispatch(handleError({ error }));
    });
  };

export const activateInsurance =
  ({ patientInsuranceId, mpi }) =>
  (dispatch, getState) => {
    const insurances = getState().patient[mpi]?.insurances || [];
    const oldInsuranceState = find(insurances, { patientInsuranceId });
    const insurance = { ...oldInsuranceState, isActive: true, mpi };

    dispatch(
      patientActions.loadInsurances({
        mpi,
        insurances: [
          ...reject(insurances, { patientInsuranceId: oldInsuranceState.patientInsuranceId }),
          insurance,
        ],
      })
    );

    return updatePatientInsuranceApi({
      insurance: omit(insurance, ['verification', 'modifiedTimestamp']),
    }).catch((error) => {
      dispatch(getPatientInsurances({ mpi }));
      dispatch(handleError({ error }));
    });
  };

export const onboardPatient =
  ({ onboardPatientInput }) =>
  (dispatch) => {
    return onboardPatientApi({ onboardPatientInput })
      .then(({ patient, authorization }) => {
        dispatch(patientActions.loadPatientData({ patient }));
        dispatch(patientActions.loadAuthorizations({ mpi: patient.mpi, authorization }));
        return true;
      })
      .catch((error) => {
        dispatch(handleError({ error }));
        return false;
      });
  };

export const getFullPatientDetails =
  ({ mpi }) =>
  (dispatch) => {
    return getFullPatientDetailsApi({ mpi })
      .then(({ guarantors, insurances, medications, authorization }) => {
        dispatch(patientActions.loadInsurances({ mpi, insurances }));
        dispatch(patientActions.loadAuthorizations({ mpi, authorization }));
        dispatch(patientActions.loadMedications({ mpi, medications }));
        dispatch(patientActions.loadGuarantors({ guarantors, mpi }));
      })
      .catch((error) => {
        dispatch(handleError({ error }));
      });
  };

export const getDuplicatePatients =
  ({ patientId }) =>
  (dispatch) => {
    return getDuplicatePatientsApi({ patientId })
      .then((response) => {
        dispatch(
          patientActions.loadDuplicatePatients({ patientId, duplicatePatientRecords: response })
        );

        map(response, (record) => {
          dispatch(
            patientActions.loadPrescriptions({
              mpi: record.patientId,
              prescriptions: record.prescriptions ?? [],
            })
          );
        });

        return response;
      })
      .catch((error) => {
        dispatch(handleError({ error }));
      });
  };

export const addPatientProvider =
  ({ provider }) =>
  (dispatch, getState) => {
    const providers = getState().patient[provider.mpi]?.providers || [];

    return addPatientProviderApi({ provider })
      .then((newProvider) => {
        dispatch(
          patientActions.loadProviders({
            mpi: provider.mpi,
            providers: [...providers, newProvider],
          })
        );
        return true;
      })
      .catch((error) => {
        dispatch(handleError({ error }));
      });
  };

export const getPatientProviders =
  ({ mpi }) =>
  (dispatch) => {
    return getPatientProvidersApi({ mpi })
      .then((providers) => dispatch(patientActions.loadProviders({ mpi, providers })))
      .catch((error) => dispatch(handleError({ error })));
  };

export const updatePatientProvider =
  ({ provider, mpi }) =>
  (dispatch, getState) => {
    const providers = getState().patient[mpi]?.providers || [];

    return updatePatientProviderApi({ provider })
      .then((updatedProvider) => {
        dispatch(
          patientActions.loadProviders({
            mpi,
            providers: [
              ...reject(providers, { patientProviderId: updatedProvider.patientProviderId }),
              updatedProvider,
            ],
          })
        );
        return true;
      })
      .catch((error) => {
        dispatch(getPatientProviders({ mpi }));
        dispatch(handleError({ error }));
        return false;
      });
  };

export const deactivatePatientProvider =
  ({ patientProviderId, mpi }) =>
  (dispatch, getState) => {
    const providers = getState().patient[mpi]?.providers || [];
    const oldProviderState = find(providers, { patientProviderId });

    dispatch(
      patientActions.loadProviders({
        mpi,
        providers: [
          ...reject(providers, { patientProviderId: oldProviderState.patientProviderId }),
          { ...oldProviderState, isActive: false },
        ],
      })
    );

    return deactivatePatientProviderApi({ patientProviderId }).catch((error) => {
      dispatch(patientActions.loadProviders({ mpi, providers }));
      dispatch(handleError({ error }));
    });
  };

export const activatePatientProvider =
  ({ patientProviderId, mpi }) =>
  (dispatch, getState) => {
    const providers = getState().patient[mpi]?.providers || [];
    const oldProviderState = find(providers, { patientProviderId });
    const provider = { ...oldProviderState, isActive: true };

    dispatch(
      patientActions.loadProviders({
        mpi,
        providers: [
          ...reject(providers, { patientProviderId: oldProviderState.patientProviderId }),
          provider,
        ],
      })
    );

    return updatePatientProviderApi({
      provider: omit(provider, 'verificationStatus'),
    }).catch((error) => {
      dispatch(getPatientProviders({ mpi }));
      dispatch(handleError({ error }));
    });
  };

export const addPatientPharmacy =
  ({ pharmacy }) =>
  (dispatch, getState) => {
    const pharmacies = getState().patient[pharmacy.mpi]?.pharmacies || [];

    return addPatientPharmacyApi({ pharmacy })
      .then((newPharmacy) => {
        dispatch(
          patientActions.loadPharmacies({
            mpi: pharmacy.mpi,
            pharmacies: [...pharmacies, newPharmacy],
          })
        );
        return true;
      })
      .catch((error) => {
        dispatch(handleError({ error }));
        return false;
      });
  };

export const getPatientPharmacies =
  ({ mpi }) =>
  (dispatch) => {
    return getPatientPharmaciesApi({ mpi })
      .then((pharmacies) => dispatch(patientActions.loadPharmacies({ mpi, pharmacies })))
      .catch((error) => dispatch(handleError({ error })));
  };

export const updatePatientPharmacy =
  ({ pharmacy, mpi }) =>
  (dispatch, getState) => {
    const pharmacies = getState().patient[mpi]?.pharmacies || [];

    return updatePatientPharmacyApi({ pharmacy })
      .then((updatedPharmacy) => {
        dispatch(
          patientActions.loadPharmacies({
            mpi,
            pharmacies: [
              ...reject(pharmacies, { patientPharmacyId: updatedPharmacy.patientPharmacyId }),
              updatedPharmacy,
            ],
          })
        );
        return true;
      })
      .catch((error) => {
        dispatch(getPatientPharmacies({ mpi }));
        dispatch(handleError({ error }));
        return false;
      });
  };

export const deletePatientPharmacy =
  ({ patientPharmacyId, mpi }) =>
  (dispatch, getState) => {
    const pharmacies = getState().patient[mpi]?.pharmacies || [];

    dispatch(
      patientActions.loadPharmacies({
        mpi,
        pharmacies: reject(pharmacies, { patientPharmacyId }),
      })
    );

    return deletePatientPharmacyApi({ patientPharmacyId }).catch((error) => {
      dispatch(patientActions.loadPharmacies({ mpi, pharmacies }));
      dispatch(handleError({ error }));
    });
  };

export const listPatientInvoices =
  ({ mpi, page }) =>
  (dispatch) => {
    dispatch(patientActions.updateInvoicesCurrentPage({ mpi, page }));

    return listPatientInvoicesApi({ mpi, page })
      .then(({ count, invoices }) => {
        dispatch(patientActions.loadInvoices({ mpi, page, invoices, count }));
      })
      .catch((error) => dispatch(handleError({ error })));
  };

export const saveTestClaim =
  ({ saveTestClaimResultInput }) =>
  (dispatch, getState) => {
    const insurances = getState().patient[saveTestClaimResultInput.mpi]?.insurances || [];

    return saveTestClaimResultApi({
      saveTestClaimResultInput,
    })
      .then(({ patientDemographics, systemIds, lastUsedInsurance }) => {
        dispatch(
          patientActions.loadPatientData({
            patient: {
              mpi: saveTestClaimResultInput.mpi,
              status: patientDemographics.status,
              systemIds,
            },
          })
        );

        dispatch(
          patientActions.loadInsurances({
            mpi: saveTestClaimResultInput.mpi,
            insurances: [
              ...reject(insurances, { patientInsuranceId: lastUsedInsurance.patientInsuranceId }),
              lastUsedInsurance,
            ],
          })
        );
      })
      .catch((error) => dispatch(handleError({ error })));
  };

export const updatePatientPartner =
  ({ mpi, partnerId, identityValue }) =>
  (dispatch) => {
    return setPartnerExternalIdApi({ mpi, partnerId, identityValue })
      .then((updatedPatientPartner) => {
        dispatch(patientActions.updatePartner({ updatedPatientPartner, mpi }));
        return true;
      })
      .catch((error) => {
        dispatch(handleError({ error }));
        return false;
      });
  };

// Reloads the patient on success so the profile shows the contacts as the server saved them.
// A rejection that names its fields (errorData) goes back to the dialog to mark them; nothing was
// saved then. Anything else goes to the shared error dialog.
export const setPatientContactsForPartners =
  ({ mpi, partnerContacts }) =>
  (dispatch) =>
    setPatientContactsForPartnersApi({ mpi, partnerContacts })
      .then(() => dispatch(getPatient({ mpi })))
      .then(() => ({ isSaved: true, rejectedFields: [] }))
      .catch((error) => {
        const { code, errorData } = first(error.response?.errors)?.extensions || {};
        const isFieldRejection =
          code === errorHeaders.INPUT_VALIDATION_ERROR && isArray(errorData) && !!errorData.length;

        if (isFieldRejection) {
          return { isSaved: false, rejectedFields: errorData };
        }

        dispatch(handleError({ error }));
        return { isSaved: false, rejectedFields: [] };
      });

export const getPatientActionItems =
  ({ mpi }) =>
  (dispatch) => {
    return getPatientActionItemsApi({ mpi })
      .then(({ requests, orders }) => {
        dispatch(
          patientActions.loadRequests({
            mpi,
            requests,
          })
        );
        dispatch(patientActions.loadOrders({ mpi, orders }));
      })
      .catch((error) => dispatch(handleError({ error })));
  };

export const controlledSubstancesCheck =
  ({ mpi, requestId, checkInput }) =>
  (dispatch) => {
    return controlledSubstancesCheckApi({ checkInput })
      .then((requestItem) =>
        dispatch(patientActions.updateTransferRequestStatus({ mpi, requestId, requestItem }))
      )
      .catch((error) => dispatch(handleError({ error })));
  };

export const reviewLineItem =
  ({ mpi, lineItemId, reviewResult, reasonForRejection, qs1RxNumber, requestId }) =>
  (dispatch) => {
    return reviewLineItemApi({ mpi, lineItemId, reviewResult, reasonForRejection, qs1RxNumber })
      .then((requestItem) => {
        dispatch(
          patientActions.updateTransferRequestStatus({
            mpi,
            requestId,
            requestItem,
          })
        );

        if (requestItem.status === 'PRODUCT_FULFILLMENT' && reviewResult) {
          dispatch(patientActions.addLineItemsToOrder({ mpi, requestItem }));
        }
      })
      .catch((error) => dispatch(handleError({ error })));
  };

export const prescriberClarificationCheck =
  ({ mpi, requestId, checkInput }) =>
  (dispatch) => {
    return prescriberClarificationCheckApi({ checkInput })
      .then((requestItem) =>
        dispatch(patientActions.updateTransferRequestStatus({ mpi, requestId, requestItem }))
      )
      .catch((error) => dispatch(handleError({ error })));
  };

export const copayResultCheck =
  ({ mpi, requestId, checkInput }) =>
  (dispatch) => {
    return copayResultCheckApi({ checkInput })
      .then((requestItem) =>
        dispatch(patientActions.updateTransferRequestStatus({ mpi, requestId, requestItem }))
      )
      .catch((error) => dispatch(handleError({ error })));
  };

export const profitabilityResultCheck =
  ({ mpi, requestId, checkInput }) =>
  (dispatch) => {
    return profitabilityResultCheckApi({ checkInput })
      .then((requestItem) =>
        dispatch(patientActions.updateTransferRequestStatus({ mpi, requestId, requestItem }))
      )
      .catch((error) => dispatch(handleError({ error })));
  };

export const verifyRequestLineItem =
  ({ input, requestId }) =>
  (dispatch) => {
    return verifyRequestLineItemApi({ input })
      .then((requestItem) => {
        dispatch(
          patientActions.updateTransferRequestStatus({
            mpi: input.mpi,
            requestId,
            requestItem,
          })
        );

        if (!requestItem.taggedForReview) {
          dispatch(patientActions.addLineItemsToOrder({ mpi: input.mpi, requestItem }));
        }

        return true;
      })
      .catch((error) => {
        dispatch(handleError({ error }));
        return false;
      });
  };

export const getPatientAddresses =
  ({ mpi }) =>
  (dispatch) => {
    return getPatientAddressesApi({ mpi })
      .then((addresses) => {
        dispatch(patientActions.loadAddresses({ mpi, addresses }));
      })
      .catch((error) => dispatch(handleError({ error })));
  };

const buildPatientAddress = ({ existingAddresses, isNewAddressDefaulted }) => {
  const updatedExistingAddress = map(existingAddresses, ({ isDefault, ...existingAddress }) => ({
    ...existingAddress,
    isDefault: isNewAddressDefaulted ? false : isDefault,
  }));

  return updatedExistingAddress;
};

export const addPatientAddress =
  ({ mpi, address }) =>
  (dispatch, getState) => {
    return addPatientAddressApi({ mpi, address })
      .then((addAddressResponse) => {
        // eslint-disable-next-line no-underscore-dangle
        if (addAddressResponse?.__typename === 'Addresses') {
          return addAddressResponse.addresses;
        }

        // Re-adding an unchanged address returns the existing row (same patientAddressId)
        // instead of a new one — drop it from the existing list so it is not shown twice.
        const existingAddresses = reject(getState().patient[mpi]?.addresses || [], {
          patientAddressId: addAddressResponse.patientAddressId,
        });
        const isNewAddressDefaulted = address.isDefault;
        const updatedExistingAddress = buildPatientAddress({
          existingAddresses,
          isNewAddressDefaulted,
        });
        dispatch(
          patientActions.loadAddresses({
            mpi,
            addresses: [addAddressResponse, ...updatedExistingAddress],
          })
        );

        // The backend normalizes the address array on every write (collapses
        // duplicate rows, moves the DEFAULT tag) — always refetch so the UI
        // reflects what was actually persisted, not the local merge.
        dispatch(getPatientAddresses({ mpi }));

        return true;
      })
      .catch((error) => {
        if (error?.response?.errors[0]?.extensions?.code === 'NO_MATCHING_ADDRESS_FOUND') {
          throw error;
        }

        dispatch(handleError({ error }));
      });
  };

export const deletePatientAddress =
  ({ mpi, patientAddressId }) =>
  (dispatch, getState) => {
    const existingAddresses = getState().patient[mpi]?.addresses;
    const updatedAddresses = reject(existingAddresses, { patientAddressId });

    dispatch(
      patientActions.loadAddresses({
        mpi,
        addresses: updatedAddresses,
      })
    );

    return deletePatientAddressApi({ mpi, patientAddressId })
      .then((response) => {
        // Reload from the backend — deletes may also collapse duplicate rows
        // or reassign the DEFAULT tag during normalization.
        dispatch(getPatientAddresses({ mpi }));

        return response;
      })
      .catch((error) => {
        dispatch(
          patientActions.loadAddresses({
            mpi,
            addresses: existingAddresses,
          })
        );

        dispatch(handleError({ error }));
      });
  };

export const updatePatientAddress =
  ({ mpi, address }) =>
  (dispatch, getState) => {
    const patientAddresses = reject(getState().patient[mpi]?.addresses, {
      patientAddressId: address.patientAddressId,
    });

    return updatePatientAddressApi({ mpi, address })
      .then((updatedAddress) => {
        dispatch(
          patientActions.loadAddresses({
            mpi,
            addresses: [updatedAddress, ...patientAddresses],
          })
        );

        // Reload from the backend — edits may collapse duplicate rows or move
        // the DEFAULT tag during normalization.
        dispatch(getPatientAddresses({ mpi }));

        return true;
      })
      .catch((error) => {
        if (error?.response?.errors[0]?.extensions?.code === 'NO_MATCHING_ADDRESS_FOUND') {
          throw error;
        }
        dispatch(handleError({ error }));

        return false;
      });
  };

export const defaultPatientAddress =
  ({ mpi, patientAddressId }) =>
  (dispatch, getState) => {
    const patientAddresses = getState().patient[mpi]?.addresses;

    const updatedAddresses = map(patientAddresses, (patientAddress) => ({
      ...patientAddress,
      isDefault: patientAddress.patientAddressId === patientAddressId,
    }));

    dispatch(
      patientActions.loadAddresses({
        mpi,
        addresses: updatedAddresses,
      })
    );

    return updatePatientAddressApi({ mpi, address: { patientAddressId, isDefault: true } })
      .then((response) => {
        // Reload from the backend — the DEFAULT tag move is normalized server-side.
        dispatch(getPatientAddresses({ mpi }));

        return response;
      })
      .catch((error) => {
        // Refetch to restore backend truth (the previous no-op restore never
        // dispatched the action).
        dispatch(getPatientAddresses({ mpi }));

        dispatch(handleError({ error }));
      });
  };

const hasPinnedNote = (notes) => some(notes, ['noteState', 'PIN']);

export const getExisitingNotes = ({ patientRecord, lineItemId, vaultId, vaultType }) => {
  if (vaultType === ContextType.LINE_ITEM) {
    const activeOrderNotes =
      find(patientRecord?.orders?.find(({ orderId }) => orderId === vaultId)?.lineItems || [], {
        lineItemId,
      })?.notes || [];

    const orderHistoryNotes =
      find(
        patientRecord?.orderHistory?.find(({ orderId }) => orderId === vaultId)?.lineItems || [],
        { lineItemId }
      )?.notes || [];

    return [...activeOrderNotes, ...orderHistoryNotes];
  }

  return patientRecord?.requests?.[vaultId]?.transferRequest?.[lineItemId]?.notes || [];
};

export const addLineItemNote =
  ({ mpi, content, commentContext, vaultId, vaultType }) =>
  (dispatch, getState) => {
    const patientRecord = getState().patient[mpi];
    const existingNotes = getExisitingNotes({
      patientRecord,
      lineItemId: commentContext.id,
      vaultId,
      vaultType,
    });

    return createLineItemNoteApi({ mpi, content, commentContext })
      .then((newNote) => {
        const updatedNotes = [newNote, ...existingNotes];

        dispatch(
          patientActions.loadLineItemNotes({
            mpi,
            notes: updatedNotes,
            vaultId,
            vaultType,
            lineItemId: commentContext.id,
            hasPinnedNote: hasPinnedNote(updatedNotes),
          })
        );

        return true;
      })
      .catch((error) => dispatch(handleError({ error })));
  };

export const listLineItemNotes =
  ({ mpi, lineItemId, vaultId, vaultType }) =>
  (dispatch) => {
    const filter = { noteContext: { noteContextIds: [lineItemId], type: ContextType.LINE_ITEM } };

    return listLineItemNotesApi({ mpi, filter })
      .then((notes) => {
        dispatch(
          patientActions.loadLineItemNotes({
            mpi,
            notes,
            vaultId,
            vaultType,
            lineItemId,
            hasPinnedNote: hasPinnedNote(notes),
          })
        );

        return true;
      })
      .catch((error) => dispatch(handleError({ error })));
  };

export const updateNoteStatus =
  ({ noteId, action: actionState, mpi, vaultId, vaultType, lineItemId }) =>
  (dispatch, getState) => {
    const patientRecord = getState().patient[mpi];
    const existingNotes = getExisitingNotes({
      patientRecord,
      lineItemId,
      vaultId,
      vaultType,
    });

    const updatedNotes = map(existingNotes, (note) =>
      note.noteId === noteId ? { ...note, noteState: actionState } : note
    );

    dispatch(
      patientActions.loadLineItemNotes({
        mpi,
        vaultId,
        vaultType,
        lineItemId,
        notes: updatedNotes,
        hasPinnedNote: hasPinnedNote(updatedNotes),
      })
    );

    return noteActionApi({ noteId, action: actionState }).catch((error) => {
      dispatch(
        patientActions.loadLineItemNotes({
          mpi,
          lineItemId,
          notes: updatedNotes,
          hasPinnedNote: hasPinnedNote(existingNotes),
          vaultId,
          vaultType,
        })
      );

      dispatch(handleError({ error }));
    });
  };

export const updateLineItemStatus =
  ({ lineItemId, mpi, orderId, state, lotAllocations, unassignedFill }) =>
  (dispatch, getState) => {
    return manageProductFulfillmentApi({
      lineItemId,
      mpi,
      orderId,
      state,
      lotAllocations,
      unassignedFill,
    })
      .then((order) => {
        const { status, lineItems } = order;

        const patientOrders = getState().patient[mpi]?.orders || [];

        const orders = map(patientOrders, (patientOrder) => {
          if (patientOrder.orderId === orderId) {
            const updatedLineItems = map(lineItems, (updatedLineItem) => {
              const matchedLineItem = find(patientOrder.lineItems, {
                lineItemId: updatedLineItem.lineItemId,
              });

              return {
                ...updatedLineItem,
                ...(matchedLineItem?.tags && { tags: matchedLineItem.tags }),
                ...(matchedLineItem?.shippingAddress && {
                  shippingAddress: matchedLineItem.shippingAddress,
                }),
                ...(matchedLineItem?.shippingOption && {
                  shippingOption: matchedLineItem.shippingOption,
                }),
                ...(matchedLineItem?.ndc && { ndc: matchedLineItem.ndc }),
                // manageProductFulfillmentStatus doesn't return productId, so without
                // this fallback it gets wiped to undefined here — breaking a later
                // Fill Product re-open for this item (FillProductLotDialog needs it
                // to call listAvailableLotsForDispense).
                ...(matchedLineItem?.productId && { productId: matchedLineItem.productId }),
                ...(matchedLineItem?.prescriberName && {
                  prescriberName: matchedLineItem.prescriberName,
                }),
                ...(matchedLineItem?.verificationStatus && {
                  verificationStatus: matchedLineItem.verificationStatus,
                }),
              };
            });

            return {
              ...order,
              status,
              lineItems: updatedLineItems,
              reshipmentOf: patientOrder?.reshipmentOf,
              fillLaterOf: patientOrder?.fillLaterOf,
              practice: patientOrder?.practice,
              partner: patientOrder?.partner,
              referenceId: patientOrder?.referenceId,
            };
          }

          return patientOrder;
        });

        dispatch(patientActions.loadOrders({ mpi, orders }));

        return true;
      })
      .catch((error) => {
        dispatch(handleError({ error }));
      });
  };

export const attachProductToNewOrder =
  ({ lineItemId, mpi, orderId, state, failureReason }) =>
  (dispatch, getState) => {
    const patientOrders = getState().patient[mpi]?.orders || [];

    return manageProductFulfillmentApi({
      lineItemId,
      mpi,
      orderId,
      state,
      failureReason,
    })
      .then((newOrder) => {
        const updatedOrders = reduce(
          patientOrders,
          (results, order) => {
            const lineItems = reject(order.lineItems, { lineItemId });

            return [
              ...results,
              {
                ...order,
                lineItems,
                status: every(lineItems, { lineItemStatus: 'PRODUCT_FULFILLED' })
                  ? 'ORDER_VERIFICATION'
                  : order.status,
              },
            ];
          },
          []
        );

        dispatch(patientActions.loadOrders({ mpi, orders: [...updatedOrders, newOrder] }));
        // eslint-disable-next-line no-use-before-define
        return dispatch(listPatientOrders({ mpi }));
      })
      .catch((error) => {
        dispatch(handleError({ error }));
      });
  };

export const verifyOrder =
  ({
    orderId,
    mpi,
    isAutoShipEnabled,
    lineItemIds,
    shipmentType,
    skipAddressValidation,
    carrierCode,
    trackingNumber,
    externalOrderId,
    shipmentMode,
    shipDate,
    shipTime,
  }) =>
  (dispatch, getState) => {
    return verifyOrderApi({
      mpi,
      orderId,
      isAutoShipEnabled,
      lineItemIds,
      shipmentType,
      skipAddressValidation,
      carrierCode,
      trackingNumber,
      externalOrderId,
      shipmentMode,
      shipDate,
      shipTime,
    })
      .then((response) => {
        const { status, orderCost, lineItems } = response;

        dispatch(patientActions.updateOrder({ mpi, orderId, status, orderCost }));

        const patientOrders = getState().patient[mpi]?.orders || [];
        const existingOrder = find(patientOrders, { orderId });

        map(lineItems, (lineItem) => {
          const inValidLineItemStatus = ['SPLIT', 'CANCELED'];

          if (!inValidLineItemStatus.includes(lineItem.lineItemStatus)) {
            const matchedLineItem = existingOrder
              ? find(existingOrder.lineItems, {
                  lineItemId: lineItem.lineItemId,
                })
              : null;

            dispatch(
              patientActions.updateOrderLineItem({
                ...lineItem,
                orderId,
                mpi,
                ...(matchedLineItem?.ndc && { ndc: matchedLineItem.ndc }),
                ...(matchedLineItem?.prescriberName && {
                  prescriberName: matchedLineItem.prescriberName,
                }),
                ...(matchedLineItem?.verificationStatus && {
                  verificationStatus: matchedLineItem.verificationStatus,
                }),
              })
            );
          }
        });

        return response;
      })
      .catch((error) => {
        const errorCode = error?.response?.errors?.[0]?.extensions?.code;

        if (errorCode === 'SHIPPING_ADDRESS_NOT_VALID') {
          throw error;
        }
        dispatch(handleError({ error }));
      });
  };

export const listPatientOrders =
  ({ mpi }) =>
  (dispatch) => {
    return listPatientOrdersApi({ mpi })
      .then((orders) => dispatch(patientActions.loadOrders({ mpi, orders })))
      .catch((error) => dispatch(handleError({ error })));
  };

export const createOrderShipment =
  ({
    mpi,
    lineItemIds,
    shipmentType,
    orderId,
    shipmentMode,
    carrierCode,
    externalShipmentId,
    trackingNumber,
    shipDate,
    shipTime,
    isShipmentFromPrx,
    addressDirectoryId,
    isBatchShipment,
    nameOnAddress,
  }) =>
  (dispatch) => {
    return createOrderShipmentApi({
      mpi,
      lineItemIds,
      shipmentType,
      shipmentMode,
      carrierCode,
      externalShipmentId,
      trackingNumber,
      shipDate,
      shipTime,
      isShipmentFromPrx,
      addressDirectoryId,
      isBatchShipment,
      nameOnAddress,
    })
      .then((createdOrderShipment) => {
        if (mpi && orderId) {
          dispatch(
            patientActions.updateOrder({
              mpi,
              orderId,
              status:
                createdOrderShipment?.status === 'ORDER_SHIPPED' ? 'SHIPPED' : 'ADDED_TO_SHIPMENT',
            })
          );
        }

        return !!createdOrderShipment;
      })
      .catch((error) => {
        if (mpi) {
          dispatch(listPatientOrders({ mpi }));
        }

        return first(error.response?.errors)?.extensions?.code ===
          errorHeaders.CAPTURE_AMOUNT_EXCEEDS_AUTHORIZED_AMOUNT
          ? dispatch(handleError({ error, heading: 'Payment processing error' }))
          : dispatch(handleError({ error }));
      });
  };

export const listPatientOrderShipments =
  ({ mpi }) =>
  (dispatch, getState) => {
    const shipmentFilter = getState().patient[mpi]?.shipmentHistory?.shipmentFilter;

    return listPatientOrderShipmentsApi({ mpi, filter: shipmentFilter })
      .then((shipments) => {
        dispatch(patientActions.loadShipments({ mpi, shipments, shipmentFilter }));
      })
      .catch((error) => dispatch(handleError({ error })));
  };

export const updatePatientOrderShipmentsFilter =
  ({ mpi, shipmentFilter }) =>
  (dispatch) => {
    dispatch(patientActions.loadShipments({ mpi, shipments: null, shipmentFilter }));

    return dispatch(listPatientOrderShipments({ mpi }));
  };

export const getPatientUsers =
  ({ mpi }) =>
  (dispatch) => {
    return getPatientUsersApi({ mpi })
      .then((patientUsers) => dispatch(patientActions.loadPatientUsers({ mpi, patientUsers })))
      .catch((error) => dispatch(handleError({ error })));
  };

export const resetPassword =
  ({ user }) =>
  (dispatch) => {
    return resetPasswordApi({ userId: user })
      .then((response) => response)
      .catch((error) => dispatch(handleError({ error })));
  };
export const cancelOrder =
  ({ orderId, cancelReason, note, mpi }) =>
  (dispatch) => {
    const cancelNote = {
      commentContext: { id: orderId, type: 'ORDER' },
      content: { contentType: 'TEXT', message: note },
      mpi,
    };

    return cancelOrderApi({ cancelReason, orderId, cancelNote })
      .then((canceledOrder) => {
        dispatch(patientActions.removeOrder({ mpi, orderId: canceledOrder.orderId }));

        return !!canceledOrder;
      })
      .catch((error) => dispatch(handleError({ error })));
  };

export const cancelOrderLineItem =
  ({ orderId, cancelReason, lineItemId, note, mpi }) =>
  (dispatch, getState) => {
    const patientOrders = getState().patient[mpi]?.orders;
    const orderLineItems = find(patientOrders, { orderId })?.lineItems || [];
    const activeLineItems = lodashFilter(
      orderLineItems,
      (lineItem) =>
        lineItem.lineItemStatus !== lineItemEnum.CANCELED &&
        lineItem.lineItemStatus !== lineItemEnum.SPLIT
    );

    const cancelNote = {
      commentContext: { id: lineItemId, type: ContextType.LINE_ITEM },
      content: { contentType: ContentType.TEXT, message: note },
      noteState: NoteState.PIN,
      mpi,
    };

    return cancelLineItemApi({ cancelReason, cancelNote, lineItemId, orderId })
      .then(() => {
        if (activeLineItems.length === 1) {
          dispatch(patientActions.removeOrder({ mpi, orderId }));
          return { isOrderCanceled: true };
        }

        dispatch(
          patientActions.updateOrderLineItem({
            lineItemId,
            status: lineItemEnum.CANCELED,
            orderId,
            mpi,
            hasPinnedNote: true,
            processingState: 'CANCELED',
          })
        );

        dispatch(listPatientOrders({ mpi }));

        return { isOrderCanceled: false };
      })
      .catch((error) => dispatch(handleError({ error })));
  };

// POM-6148: split a line item's quantity into one or more fill-later schedules. The
// original drops to the retained quantity and is marked VOID; each split is recorded at
// SCHEDULED and its order is created later by the batch job (POM-6150).
//
// The mutation returns the fill_later_item rows as stored — a flat array, ordered by
// scheduledFor — and nothing about the line item itself, which the refetch below picks up.
export const splitLineItemQuantity =
  ({ mpi, items }) =>
  (dispatch) =>
    splitLineItemQuantityApi({ items })
      .then((fillLaterItems) => {
        // POM-6199: the response is one flat list for the whole submission, so it is split
        // back out per line item — the reducer holds the rows on the item they belong to.
        forEach(items, ({ orderId, lineItemId }) => {
          dispatch(
            patientActions.updateOrderLineItemFillLater({
              mpi,
              orderId,
              lineItemId,
              fillLaterItems: lodashFilter(fillLaterItems, { referenceItemId: lineItemId }),
            })
          );
        });

        // The quantity, the line item status, the add-ons and the Rx label are all changed
        // server side, so refetch rather than trying to mirror those changes here.
        dispatch(listPatientOrders({ mpi }));

        return fillLaterItems;
      })
      .catch((error) => dispatch(handleError({ error })));

export const moveLineItemsToFillLater =
  ({ mpi, items }) =>
  (dispatch) =>
    moveLineItemsToFillLaterApi({ items })
      .then((moved) => {
        // The items leave their order, their processing state changes and the labels are
        // rebuilt server side, so refetch rather than mirroring any of that here.
        dispatch(listPatientOrders({ mpi }));

        return moved;
      })
      .catch((error) => dispatch(handleError({ error })));

export const editPatientPrescription =
  ({ prescriptionId, action, mpi, autofillValue }) =>
  (dispatch) =>
    editPatientPrescriptionApi({ action, prescriptionId, autoFill: autofillValue })
      .then(({ isActive, autofill }) =>
        dispatch(
          patientActions.updatePrescriptionState({
            mpi,
            prescriptionId,
            isActive,
            autofill,
          })
        )
      )
      .catch((error) => dispatch(handleError({ error })));

export const completeEntry =
  ({ mpi, requestId, lineItemId, state }) =>
  (dispatch) =>
    completeEntryApi({ lineItemId, mpi, state })
      .then((requestItem) =>
        dispatch(patientActions.updateTransferRequestStatus({ mpi, requestId, requestItem }))
      )
      .catch((error) => dispatch(handleError({ error })));

export const assertQs1RxNumber =
  ({ mpi, rxNumber, lineItemId, requestId }) =>
  (dispatch) =>
    assertQs1RxNumberApi({ lineItemId, mpi, rxNumber })
      .then((requestItem) => {
        dispatch(patientActions.updateTransferRequestStatus({ mpi, requestId, requestItem }));
        return true;
      })
      .catch((error) => {
        dispatch(handleError({ error }));
        return false;
      });

export const searchQS1Profile =
  ({ firstName, lastName, dob, mpi }) =>
  (dispatch) =>
    searchQs1ProfileApi({ dob, firstName, lastName }).then((qs1Profiles) => {
      dispatch(patientActions.loadPatientQs1Profile({ mpi, qs1Profiles }));
    });

export const associatePatientWithExternalProfile =
  ({ mpi, externalProfile }) =>
  (dispatch) => {
    return associatePatientWithExternalProfileApi({ externalProfile, mpi })
      .then((systemIds) => {
        dispatch(patientActions.loadSystemIds({ mpi, systemIds }));
        return true;
      })
      .catch((error) => {
        dispatch(handleError({ error }));
        return false;
      });
  };

export const updateRequestLineItemState =
  ({ mpi, requestId, lineItemId, currentState, skip, rollback }) =>
  (dispatch) =>
    updateRequestLineItemStateApi({
      mpi,
      requestId,
      lineItemId,
      currentState,
      skipState: skip,
      rollbackState: rollback,
    })
      .then((requestItem) =>
        dispatch(patientActions.updateTransferRequestStatus({ mpi, requestId, requestItem }))
      )
      .catch((error) => dispatch(handleError({ error })));

export const listPrescriptionNotes =
  ({ contextId, contextType, mpi, isPrescriptionReview }) =>
  (dispatch) => {
    const filter = { noteContext: { noteContextIds: [contextId], type: contextType } };

    return listLineItemNotesApi({ mpi, filter, isPrescriptionReview })
      .then((notes) => {
        if (!isPrescriptionReview) {
          dispatch(
            patientActions.loadPrescriptionNotes({
              mpi,
              notes,
              contextId,
            })
          );
        } else {
          dispatch(
            workQueueActions.loadReviewPrescriptionNotes({
              notes,
              contextId,
            })
          );
        }

        return true;
      })
      .catch((error) => dispatch(handleError({ error })));
  };

export const addPrescriptionNotes =
  ({ mpi, content, commentContext, contextId, isPrescriptionReview }) =>
  (dispatch, getState) => {
    const prescriptions = getState().patient[mpi]?.prescriptions || [];
    const existingNotes = find(prescriptions, { prescriptionUUID: contextId })?.notes || [];

    return createLineItemNoteApi({ mpi, content, commentContext })
      .then((newNote) => {
        if (!isPrescriptionReview) {
          dispatch(
            patientActions.loadPrescriptionNotes({
              mpi,
              notes: [...existingNotes, newNote],
              contextId,
            })
          );
        } else {
          const prescriptionFromWorkQueue = getState().workQueue?.prescriptionReview;
          const currentPage = prescriptionFromWorkQueue?.currentPage;
          const prescriptionObject = find(prescriptionFromWorkQueue?.pages[currentPage], {
            pomPrescriptionId: contextId,
          });
          const existingPrescriptionReviewNotes = prescriptionObject?.notes;

          dispatch(
            workQueueActions.loadReviewPrescriptionNotes({
              notes: [...existingPrescriptionReviewNotes, newNote],
              contextId,
            })
          );
        }

        return true;
      })
      .catch((error) => dispatch(handleError({ error })));
  };

export const markOrderForExternalProcessing =
  ({ mpi, orderId }) =>
  (dispatch) =>
    orderNeedsReviewApi({ mpi, orderId })
      .then((isMarkedReview) => {
        if (isMarkedReview) {
          dispatch(patientActions.removeOrder({ mpi, orderId }));
        }

        return isMarkedReview;
      })
      .catch((error) => dispatch(handleError({ error })));

export const updateOrderAddress =
  ({ mpi, orderId, addressInput }) =>
  (dispatch, getState) => {
    return updateOrderAddressApi({ addressInput, mpi, orderId })
      .then((updateOrderAddressResponse) => {
        // eslint-disable-next-line no-underscore-dangle
        if (updateOrderAddressResponse?.__typename === 'Addresses') {
          return updateOrderAddressResponse.addresses;
        }

        const existingAddresses = getState().patient[mpi]?.addresses || [];
        const updatedPatientAddressId = updateOrderAddressResponse.patientAddressId;
        const existingAddressesExcludingUpdated = reject(
          existingAddresses,
          (existing) =>
            (updatedPatientAddressId && existing.patientAddressId === updatedPatientAddressId) ||
            isDuplicatesFound([existing.address], updateOrderAddressResponse.address)
        );
        const isNewAddressDefaulted = addressInput.isDefault;
        const updatedExistingAddress = buildPatientAddress({
          existingAddresses: existingAddressesExcludingUpdated,
          isNewAddressDefaulted,
        });

        const shippingAddress = {
          nameOnAddress: updateOrderAddressResponse.nameOnAddress,
          line1: updateOrderAddressResponse.address.addressLine1,
          line2: updateOrderAddressResponse.address.addressLine2,
          city: updateOrderAddressResponse.address.city,
          state: updateOrderAddressResponse.address.state,
          zip: updateOrderAddressResponse.address.zip,
        };

        dispatch(
          patientActions.updateOrderAddress({
            mpi,
            orderId,
            shippingAddress,
            deliveryInstructions: updateOrderAddressResponse.deliveryInstructions,
          })
        );
        dispatch(
          patientActions.loadAddresses({
            mpi,
            addresses: [updateOrderAddressResponse, ...updatedExistingAddress],
          })
        );

        return !!updateOrderAddressResponse;
      })
      .catch((error) => {
        const errorCode = error?.response?.errors?.[0]?.extensions?.code;

        if (errorCode === 'NO_MATCHING_ADDRESS_FOUND') {
          throw error;
        }
        dispatch(handleError({ error }));
      });
  };

export const markPrescriptionReview =
  ({ patientId, prescriptionUUIDs, lineItemIds, orderId, reviewTarget }) =>
  (dispatch) => {
    return prescriptionNeedsReviewAPI({ patientId, prescriptionUUIDs, reviewTarget })
      .then((response) => {
        if (response) {
          dispatch(
            patientActions.updateOrderLineItemTaggedReview({
              patientId,
              orderId,
              lineItemIds,
              taggedForReview: true,
            })
          );
        }

        return response;
      })
      .catch((error) => dispatch(handleError({ error })));
  };

export const updatePrescriptionReviewReason =
  ({ patientId, prescriptionUUID, lineItemId, reviewTarget }) =>
  (dispatch) => {
    return changePrescriptionReviewReasonApi({
      patientId,
      prescriptionUUID,
      lineItemId,
      reviewTarget,
    })
      .then((response) => {
        dispatch(getPrescriptionsNeedsReviewByPage({ page: 1 }));
        return response;
      })
      .catch((error) => dispatch(handleError({ error })));
  };

export const reverifyOrder =
  ({ orderId, mpi }) =>
  (dispatch) => {
    return reverifyOrderApi({ orderId, mpi })
      .then(({ status }) => {
        dispatch(patientActions.updateOrder({ mpi, orderId, status }));
        dispatch(listPatientOrders({ mpi }));

        return !!status;
      })
      .catch((error) => dispatch(handleError({ error })));
  };

export const editOrderShipment =
  ({ input, mpi, suppressError = false }) =>
  (dispatch) => {
    return editOrderShipmentApi(input)
      .then((updatedShipment) => {
        if (!input.orderId) {
          dispatch(listPatientOrderShipments({ mpi }));
        }

        return updatedShipment;
      })
      .catch((error) => {
        if (suppressError) {
          throw error;
        }
        dispatch(handleError({ error }));
      });
  };

export const validatePrescriptionForRefill = (prescriptionUUID) => (dispatch) =>
  validatePrescriptionForRefillApi({ prescriptionUUID })
    .then((response) => response)
    .catch((error) => dispatch(handleError({ error })));

export const refillOrder =
  ({ rxNumber, patientId, shippingAddress, deliveryInstructions }) =>
  (dispatch) =>
    createRefillOrderApi({ patientId, rxNumber, shippingAddress, deliveryInstructions })
      .then((response) => response)
      .catch((error) => dispatch(handleError({ error })));

export const validateAddress =
  ({ input }) =>
  (dispatch) =>
    validateAddressApi({ input }).catch((error) => {
      dispatch(handleError({ error }));

      throw error;
    });

export const getPatientOrderHistory =
  ({ mpi, status, includeAllFacilities }) =>
  (dispatch) =>
    getPatientOrderHistoryApi({ mpi, status, includeAllFacilities })
      .then((orders) => dispatch(patientActions.loadOrderHistory({ mpi, orders })))
      .catch((error) => dispatch(handleError({ error })));

export const getPrescriptionSignedUrl =
  ({ prescription }) =>
  (dispatch) => {
    return getPrescriptionSignedUrlApi({
      prescriptionUUID: prescription.prescriptionUUID,
      fileKeys: [prescription.fileKey],
    })
      .then((response) => {
        return response.getPrescriptionSignedURL;
      })
      .catch((error) => {
        dispatch(handleError({ error }));
      });
  };

export const getAllOrders =
  ({ mpi, includeAllFacilities }) =>
  (dispatch) => {
    const status = [
      'SHIPPED',
      'OPEN',
      'ORDER_VERIFICATION',
      'ORDER_VERIFIED',
      'ADDED_TO_SHIPMENT',
      'HOLD',
      'ORDER_RE_VERIFICATION',
      'READY_FOR_SHIPMENT',
    ];
    return getPatientOrderHistoryApi({ mpi, status, includeAllFacilities })
      .then((orders) => dispatch(patientActions.loadPrescriptionDialog({ mpi, orders })))
      .catch((error) => dispatch(handleError({ error })));
  };

export const voidShipment =
  ({ mpi, lineItemIds, orderId, orderStatus }) =>
  (dispatch) => {
    return voidShipmentApi({ mpi, lineItemIds, orderId, orderStatus })
      .then((response) => {
        return response;
      })
      .catch((error) => {
        dispatch(handleError({ error }));
      });
  };

export const sendOrderBackToQueue =
  ({ orderIds, rollbackType }) =>
  (dispatch) => {
    return sendOrderBackToQueueApi({ orderIds, rollbackType })
      .then((response) => {
        return response;
      })
      .catch((error) => {
        dispatch(handleError({ error }));
      });
  };

export const shipOrderLater =
  ({ orderId, shipByDate }) =>
  (dispatch) => {
    return shipOrderLaterApi({ orderId, shipByDate })
      .then((response) => response)
      .catch((error) => {
        dispatch(handleError({ error }));
      });
  };

export const getPrescriptionFillHistory =
  ({ prescriptionId }) =>
  (dispatch) => {
    return getPrescriptionFillHistoryApi({ prescriptionId })
      .then((fillHistory) => {
        return fillHistory;
      })
      .catch((error) => {
        dispatch(handleError({ error }));
      });
  };

export const recordAuditEvent =
  ({ eventType, patientId, actionType, spOrderId }) =>
  (dispatch) => {
    return recordAuditEventApi({ eventType, patientId, actionType, spOrderId }).catch((error) => {
      dispatch(handleError({ error }));
    });
  };

export const getPatientHistoryTrack =
  ({ mpi, page = 1, limit = 25, append = false }) =>
  (dispatch) => {
    return patientHistoryTrackListApi({ mpi, page, limit })
      .then((historyTrack) => {
        dispatch(patientActions.loadPatientHistoryTrack({ mpi, historyTrack, append }));
        return historyTrack;
      })
      .catch((error) => dispatch(handleError({ error })));
  };

export const resetPatientHistoryTrack =
  ({ mpi }) =>
  (dispatch) => {
    dispatch(patientActions.resetPatientHistoryTrack({ mpi }));
  };

export const updateAddOnQuantity =
  ({ lineItemId, orderId, mpi, quantity }) =>
  (dispatch) => {
    return updateAddOnQuantityApi({ lineItemId, orderId, quantity }).then((success) => {
      if (success) {
        dispatch(
          patientActions.updateLineItemAddOnQuantity({ mpi, orderId, lineItemId, quantity })
        );
      }
      return success;
    });
  };

// POM-6180: the whole order to one fill date. The server parks the order (VOID) and every line
// item (processing state VOID, flagged for fill later); the refetch below picks all of that up
// rather than mirroring it here. Order history gains the FILL_LATER_ORDER_MOVED row.
export const moveOrderToFillLater =
  ({ mpi, orderId, scheduledFor, addressDirectoryId, isManuallyVerified }) =>
  (dispatch) =>
    moveOrderToFillLaterApi({ orderId, scheduledFor, addressDirectoryId, isManuallyVerified })
      .then((result) => {
        dispatch(listPatientOrders({ mpi }));
        dispatch(
          getPatientOrderHistory({
            mpi,
            status: BASIC_ORDER_HISTORY_STATUSES,
            includeAllFacilities: true,
          })
        );

        return result;
      })
      .catch((error) => dispatch(handleError({ error })));
